import { ReactElement, ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { OfferingStatus } from '@arrived/models';

import { Constants } from './Constants';

const STR_IMAGE_CDN_URL = `${Constants.awsCdnUrl}/images/strContent`;
export const getSTRImageUrl = (fileName: string) => `${STR_IMAGE_CDN_URL}/${fileName}`;

type SharedLinkElement = ({ href, text }: { href: string; text: string }) => ReactElement;

export interface STRData {
  airBnbLink?: string;
  amenities: string[];
  market: {
    description: (SharedLinkElement: SharedLinkElement) => ReactElement;
    marketGradeImageSrc?: string;
  };
  propertyManagement: {
    name: string;
    description: ReactNode;
    imageSrc?: string;
    estimatedLaunchDate: string;
  };
  feeData: {
    aumPropertyPurchasePrice: string;
    aumGrossRevenues: string;
    pmGrossRentsText: string;
  };
  historicalData?: {
    dailyRateChartImageSrc: string;
    monthlyRevenueChartImageSrc: string;
    occupancyChartImageSrc: string;
  };
}

const STR_EXTRA_DATA: Record<number, STRData> = {
  // The Oasis
  185: {
    airBnbLink: 'https://www.airbnb.com/rooms/736281693275917405',
    amenities: [
      'Rooftop with a downtown view',
      'Garage parking',
      '10 minute drive to downtown',
      'Game room',
      'Karaoke & stage',
      'Rooftop cornhole',
      'Ping pong & shuffle board',
      'TV on rooftop',
    ],
    propertyManagement: {
      name: 'Misfit Homes',
      description:
        'Misfit Homes is a vacation rental company based in Nashville, TN specializing in what they refer to as “fun luxury stays”. An Airbnb Superhost with over 670 reviews and VRBO Premier Partner, Misfit homes manages over 25 properties, each decorated with high-end furnishings, Music City inspired art, and spaces designed to elevate your stay to a uniquely memorable experience. When you stay at one of these homes, you know you will be treated to thoughtful and fun amenities, such as skyline views, games, and karaoke rooms. Stand Out and Stay In with Misfit Homes.',
      imageSrc: getSTRImageUrl('property-management-misfit.png'),
      estimatedLaunchDate: 'October 2022',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: (SharedLinkElement) => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Ranked Thrillist's “{thrillistLink}” and #5 in Trip Advisor's “{tripAdvisorLink}”, Nashville is the capital and the most populous city in Tennessee. Also known as “Music City,” Nashville is home to Dolly Parton & former King of Rock, Elvis Presley. Travelers from far and wide flock to Nashville for concerts and it is a top destination for bachelor & bachelorette parties. AirDNA ranks Nashville an A+.`}
          values={{
            br: <br />,
            thrillistLink: (
              <SharedLinkElement
                href="https://www.visitmusiccity.com/accolades-honors"
                text="Best U.S. Cities for a Weekend Trip"
              />
            ),
            tripAdvisorLink: (
              <SharedLinkElement
                href="https://www.visitmusiccity.com/accolades-honors"
                text="Best Destinations for City Lovers in the United States"
              />
            ),
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-oasis.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-oasis-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-oasis-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-oasis-occ.png'),
    },
  },
  // The Pointbreak
  186: {
    airBnbLink: 'https://www.airbnb.com/rooms/801657309144925056',
    amenities: [
      'Two separate units for privacy, can be rented together or separate',
      'Beach accessories',
      '5 min drive to beach access',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'December 2022',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => <FormattedMessage id="market-description.panama-city-beach" />,
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-pointbreak-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-pointbreak-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-pointbreak-occ.png'),
    },
  },
  // The Hammock
  187: {
    airBnbLink: 'https://www.airbnb.com/rooms/762570023330402157',
    amenities: [
      'Covered Pool',
      'Backyard hammocks',
      'On-site parking',
      '5 min drive to Florida Botanical Gardens',
      '10 min drive to Indian Rocks Beach',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'January 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: (SharedLinkElement) => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`A short 30 min drive west of Tampa, Largo is home to the {botanicalGardensLink} and, of course, easy access to world-renown Florida beaches. Whether it’s soaking up some warm Florida rays, playing golf, or visiting the local morning farmers' market, Largo has it.`}
          values={{
            br: <br />,
            botanicalGardensLink: (
              <SharedLinkElement href="https://www.flbgfoundation.org/" text="Florida Botanical Gardens" />
            ),
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-hammock.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-hammock-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-hammock-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-hammock-occ.png'),
    },
  },
  // The Ace
  188: {
    airBnbLink: 'https://www.airbnb.com/rooms/776676725784031329',
    amenities: ['Pool', 'Hot tub', 'Putting green', 'Large house for big groups', '10 min drive to the Promenade'],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'December 2022',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => <FormattedMessage id="market-description.scottsdale" />,
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-ace.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-ace-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-ace-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-ace-occ.png'),
    },
  },
  // The Cardinal
  189: {
    airBnbLink: 'https://www.airbnb.com/rooms/788292636696706010',
    amenities: [
      'Pool',
      'Fire pit',
      'Large house for big groups',
      'Big backyard for hangouts',
      '20 min walk to State Farm Stadium',
    ],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'December 2022',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: (SharedLinkElement) => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Phoenix, dubbed “LA without the Pacific” by {usNewsLink}, is the perfect place for snowbirds seeking refuge. While visiting, enjoy endless shopping, a booming bar scene, and of course, hundreds of golf courses. Treat yourself to world-class spas or grab your favorite cold refresher and chill at the pool and soak in the sun.`}
          values={{
            br: <br />,
            usNewsLink: <SharedLinkElement href="https://travel.usnews.com/Phoenix_AZ/" text="US News" />,
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-cardinal.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-cardinal-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-cardinal-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-cardinal-occ.png'),
    },
  },
  // The Orchard
  190: {
    airBnbLink: 'https://www.airbnb.com/rooms/781877986705557069',
    amenities: ['Hot tub', 'Wood deck with views', 'Large spacious build', 'Paved access', '5 min drive to town'],
    propertyManagement: {
      name: 'Southern Comfort',
      description:
        'Southern Comfort has over 10 years of experience managing cabins in the Blue Ridge region. With over 180 properties, they have a full in-house dedicated team covering everything from maintenance to turnovers and everything in between. With over 70% of their reservations handled through direct bookings, it’s clear the experience they provide is one-of-a-kind.',
      imageSrc: getSTRImageUrl('property-management-southern-comfort.png'),
      estimatedLaunchDate: 'December 2022',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '25%',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Blue Ridge is home to 106,000 acres of Chattahoochee National Forest, 300 miles of hiking trails, and 100 miles of trout streams, a true reminder of the beautiful nature that can be accessed easily, a short hour and a half drive from downtown Atlanta. Wake up to misty mornings, grab a nice cup of Joe, and sit on the balcony watching the sun rise. If this sounds like your cup of tea (or coffee), Blue Ridge is your destination.`}
          values={{
            br: <br />,
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-orchard.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-orchard-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-orchard-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-orchard-occ.png'),
    },
  },
  // The Mirage
  191: {
    amenities: ['Hot tub', 'Fire pit', 'Open space for privacy', '12 min drive to Joshua Tree National Park'],
    propertyManagement: {
      name: 'Alpha Geek Capital',
      description:
        "Tony J. Robinson is the founder and managing partner of Alpha Geek Capital. He's widely regarded as one of the leading experts on investing in vacation rentals. He's the co-host of the BiggerPockets Real Estate Rookie Podcast, a top business podcast with over 11,000,000 downloads. He and his wife, Sara, run the Real Estate Robinsons YouTube channel, with 25,000 subscribers. Along with his partners, he's created a strong track record of creating solid returns within the short-term rental asset.",
      imageSrc: getSTRImageUrl('property-management-alpha-geek.png'),
      estimatedLaunchDate: 'March 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '20%',
    },
    market: {
      description: (SharedLinkElement) => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Like something out of a Dr. Seuss book, Joshua Tree is indeed a ‘{beautifullyBizarreLink}’ escape from the city. Known for its surreal rocks and trees, this is the perfect destination for rock-climbers, hikers, and people that just need to disconnect a bit — rightfully making its claim onto {usNewsLink}.`}
          values={{
            br: <br />,
            beautifullyBizarreLink: (
              <SharedLinkElement
                href="https://www.thrillist.com/travel/nation/joshua-tree-national-park-travel-guide"
                text="beautifully bizarre"
              />
            ),
            usNewsLink: (
              <SharedLinkElement
                href="https://travel.usnews.com/rankings/best-places-to-visit-in-california/"
                text="US News' Top 25 California destinations"
              />
            ),
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-mirage.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-mirage-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-mirage-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-mirage-occ.png'),
    },
  },
  // The Cactus
  196: {
    airBnbLink: 'https://www.airbnb.com/rooms/772055951871319028',
    amenities: [
      'Entire backyard pool',
      'Large home for big groups',
      'Modern upgrade & touches',
      'Game room',
      'Driveway parking',
      '8 min drive to the Promenade',
    ],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'December 2022',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => <FormattedMessage id="market-description.scottsdale" />,
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-cactus.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-cactus-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-cactus-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-cactus-occ.png'),
    },
  },
  // The Opry
  197: {
    airBnbLink: 'https://www.airbnb.com/rooms/837644210852246972',
    amenities: [
      'Rooftop with downtown view',
      'Luxury furnishings',
      'Rooftop TV lounge',
      'Karaoke & stage',
      'Game room',
      'Ping Pong & Shuffle Board',
      'TV Lounge',
      'Garage parking',
      '5 min drive to downtown',
    ],
    propertyManagement: {
      name: 'Misfit Homes',
      description:
        'Misfit Homes is a vacation rental company based in Nashville, TN specializing in what they refer to as “fun luxury stays”. An Airbnb Superhost with over 670 reviews and VRBO Premier Partner, Misfit homes manages over 25 properties, each decorated with high-end furnishings, Music City inspired art, and spaces designed to elevate your stay to a uniquely memorable experience. When you stay at one of these homes, you know you will be treated to thoughtful and fun amenities, such as skyline views, games, and karaoke rooms. Stand Out and Stay In with Misfit Homes.',
      imageSrc: getSTRImageUrl('property-management-misfit.png'),
      estimatedLaunchDate: 'March 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: (SharedLinkElement) => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Ranked Thrillist's “{thrillistLink}” and #5 in Trip Advisor's “{tripAdvisorLink}”, Nashville is the capital and the most populous city in Tennessee. Also known as “Music City,” Nashville is home to Dolly Parton & former King of Rock, Elvis Presley. Travelers from far and wide flock to Nashville for concerts and it is a top destination for bachelor & bachelorette parties. AirDNA ranks Nashville an A+.`}
          values={{
            br: <br />,
            thrillistLink: (
              <SharedLinkElement
                href="https://www.visitmusiccity.com/accolades-honors"
                text="Best U.S. Cities for a Weekend Trip"
              />
            ),
            tripAdvisorLink: (
              <SharedLinkElement
                href="https://www.visitmusiccity.com/accolades-honors"
                text="Best Destinations for City Lovers in the United States"
              />
            ),
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-opry.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-opry-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-opry-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-opry-occ.png'),
    },
  },
  // The Lakeridge
  202: {
    amenities: [
      'Fire pit and backyard living space',
      'Wood deck',
      'Hot tub',
      'Traditional cabin theme',
      'Game room',
      'Plenty of driveway parking',
      'Paved access',
      '8 min drive to downtown Blue Ridge',
    ],
    propertyManagement: {
      name: 'Southern Comfort',
      description:
        'Southern Comfort has over 10 years of experience managing cabins in the Blue Ridge region. With over 180 properties, they have a full in-house dedicated team covering everything from maintenance to turnovers and everything in between. With over 70% of their reservations handled through direct bookings, it’s clear the experience they provide is one-of-a-kind.',
      imageSrc: getSTRImageUrl('property-management-southern-comfort.png'),
      estimatedLaunchDate: 'February 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '25%',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Blue Ridge is home to 106,000 acres of Chattahoochee National Forest, 300 miles of hiking trails, and 100 miles of trout streams, a true reminder of the beautiful nature that can be accessed easily, a short hour and a half drive from downtown Atlanta. Wake up to misty mornings, grab a nice cup of Joe, and sit on the balcony watching the sun rise. If this sounds like your cup of tea (or coffee), Blue Ridge is your destination.`}
          values={{
            br: <br />,
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-lakeridge.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-lakeridge-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-lakeridge-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-lakeridge-occ.png'),
    },
  },
  // The Serenity
  203: {
    airBnbLink: 'https://www.airbnb.com/rooms/804328788315006341',
    amenities: [
      'Gorgeous red rock views',
      'Hot tub',
      'Patio for dining and vibing',
      'Game room',
      'Spacious driveway parking',
      '10 min drive to multiple trails and parks',
    ],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'January 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="sedona-market-description"
          defaultMessage="Located in Arizona’s high desert under the towering southwestern rim of the vast Colorado Plateau, the city of Sedona is blessed with four mild seasons marked by abundant sunshine and clean air. Strategically situated at the mouth of spectacular Oak Creek Canyon, Sedona is a unique place characterized by massive red-rock formations, as well as the contrasting riparian areas of Oak Creek Canyon, the area surrounding this beloved community is considered at least as beautiful as many national parks. Learn more about why Sedona’s natural beauty goes so much deeper than the stunning rock formations. The city of Sedona is one of Arizona’s premier tourism, recreation, resort, retirement and art destinations."
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-serenity.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-serenity-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-serenity-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-serenity-occ.png'),
    },
  },
  // The Sugarcreek
  204: {
    amenities: [
      'Fire pit and backyard living space',
      'Wood deck with grill',
      'Modern cabin theme',
      'Game room',
      'Plenty driveway parking',
      'Paved access',
      '7 min drive to downtown Blue Ridge',
    ],
    propertyManagement: {
      name: 'Southern Comfort',
      description:
        'Southern Comfort has over 10 years of experience managing cabins in the Blue Ridge region. With over 180 properties, they have a full in-house dedicated team covering everything from maintenance to turnovers and everything in between. With over 70% of their reservations handled through direct bookings, it’s clear the experience they provide is one-of-a-kind.',
      imageSrc: getSTRImageUrl('property-management-southern-comfort.png'),
      estimatedLaunchDate: 'February 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '25%',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="property-details-market-description-text"
          defaultMessage={`Blue Ridge is home to 106,000 acres of Chattahoochee National Forest, 300 miles of hiking trails, and 100 miles of trout streams, a true reminder of the beautiful nature that can be accessed easily, a short hour and a half drive from downtown Atlanta. Wake up to misty mornings, grab a nice cup of Joe, and sit on the balcony watching the sun rise. If this sounds like your cup of tea (or coffee), Blue Ridge is your destination.`}
          values={{
            br: <br />,
          }}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-sugarcreek.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-sugarcreek-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-sugarcreek-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-sugarcreek-occ.png'),
    },
  },
  // The Palm
  205: {
    airBnbLink: 'https://www.airbnb.com/rooms/802202617256673680',
    amenities: [
      'Spacious pool',
      'Hot tub',
      'Tiki bar & theme',
      'Patio',
      'Game room',
      'Driveway parking',
      '10 min drive to beach access',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'January 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="west-palm-beach-market-description"
          defaultMessage={`Located along Florida’s Atlantic Ocean coast, West Palm Beach offers visitors year-round excitement within easy reach of smooth-sand beaches and some of the nation’s most enticing waterways. If you want to see some of the nation’s top musical entertainment, time your visit for SunFest – Florida’s largest waterfront music festival. Downtown West Palm Beach is home to four retail and entertainment districts: Clematis Street, Rosemary Square, Northwood Village, and Antique Row. These vibrant pieces of West Palm Beach are lined with fashionable bars, nightclubs, boutiques and collectables.`}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-palm.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-palm-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-palm-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-palm-occ.png'),
    },
  },
  // The Havasu
  208: {
    amenities: [
      'Heated pool',
      'Balcony with lake view',
      'Spacious for large groups',
      'Backyard hangout area with grill',
      'Driveway for on-site parking',
      'Game room',
      '3 mins to lake access',
    ],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'March 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="lake-havasu-city-description"
          defaultMessage={`Appropriately dubbed Arizona’s Playground, Lake Havasu City attracts more than 1 million visitors annually sitting right between the intersection of Arizona, Nevada and California. Home to 60 miles of uninterrupted navigable waterways, 300 annual events, 300 days of sunshine, 400 miles of river coastline, and 1,800 miles of off-road trails and the original London Bridge that was moved here in 1967! Enjoy summers to the fullest extent on mountain bike, off-road vehicle or discover secret lake coves in a kayak or boat - whatever your sport Lake Havasu promises an unforgettable time.`}
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-havasu.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-havasu-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-havasu-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-havasu-occ.png'),
    },
  },
  // The Regal
  213: {
    airBnbLink: 'https://www.airbnb.com/rooms/821061694288633880',
    amenities: [
      'Balcony with forest views',
      'Spacious for large groups',
      'Driveway for on-site parking',
      'Large game room in basement',
      'Over 3 acres of privacy and relaxation',
      '10 mins to restaurants and trails',
    ],
    propertyManagement: {
      name: 'AvantStay',
      description:
        'AvantStay is a premium vacation rental manager of high-end homes creating experiences that are comfortable, fun, relaxing, exciting that always make you feel right at home. Their team takes care of everything so getaways worry-free, creating unforgettable vacations, adventures and retreats, ultimately providing the perfect setting for people to come together and make memories away from the daily grind. AvantStay currently manages 400+ properties in 39 strong vacation markets and publishes to 65+ popular booking sites.',
      imageSrc: getSTRImageUrl('property-management-avantstay.png'),
      estimatedLaunchDate: 'March 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '20%',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="great-smoky-mountains-description"
          defaultMessage="The Great Smoky Mountains is America’s most visited national park, attracting over 10 million visitors per year! Called the Smokies due to the ever-present morning fog, this mountain range is world renowned for the diversity of its plant and animal life, the beauty of its ancient mountains, and its history of southern Appalachian mountain culture. Located along the Tennessee-North Carolina border and home to the country's most visited national park, the Smokies provide over 800 miles of hiking trails, rivers, and a good chance at a black bear sighting. Hike, camp, and experience one of America’s oldest mountain ranges. If you love spending time in nature, staying active, and celebrating wildlife, a trip to the Great Smoky Mountains is a must."
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-regal.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-regal-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-regal-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-regal-occ.png'),
    },
  },
  // The Lodge
  220: {
    airBnbLink: 'https://www.airbnb.com/rooms/841851304454256017',
    amenities: [
      'Hot tub',
      'Spacious for large groups',
      'Sports court',
      'Mini rock-climbing wall',
      'Fire pit',
      'Camping games',
      '15 mins to Broken Bow Lake',
    ],
    propertyManagement: {
      name: 'Cabins in Broken Bow',
      description:
        'Cabins in Broken Bow started in 2001 owning multiple and managing many more cabins and properties in the Broken Bow area. The team has over 31 years of experience in property and hotel management and prides itself with a small town, cutting edge spirit. With full in-house maintenance and cleaning teams it’s no wonder they’ve accumulated 250+ great reviews on Google.',
      estimatedLaunchDate: 'March 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '25%',
    },
    market: {
      description: () => <FormattedMessage id="market-description.broken-bow" />,
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-lodge.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-lodge-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-lodge-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-lodge-occ.png'),
    },
  },
  // The Myrtle
  236: {
    amenities: [
      'Two blocks to beach access',
      'Beach accessories',
      'In-unit washer & dryer',
      'Dual access to bottom & top floors',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'April 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="myrtle-beach-market-description"
          defaultMessage="Myrtle Beach, South Carolina is a popular coastal destination known for its beautiful beaches and lively atmosphere. The city offers an array of attractions, including amusement parks, water sports, and championship golf courses. Visitors can stroll along the famous Myrtle Beach Boardwalk, enjoy fresh seafood at local restaurants, or take in a live show at one of the many theaters. The area also features a vibrant nightlife scene, with bars and clubs that stay open late. With year-round mild weather and endless entertainment options, Myrtle Beach is a top vacation destination for families and beach-goers alike."
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-myrtle.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-myrtle-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-myrtle-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-myrtle-occ.png'),
    },
  },
  // The Kinlani
  237: {
    amenities: ['Ultra modern build', 'Fireplace', 'Hot tub', 'Deck', '30 mins to Arizona Snowbowl'],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'May 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => (
        <FormattedMessage
          id="flagstaff-market-description"
          defaultMessage="Flagstaff is a vibrant city located in northern Arizona. It's surrounded by breathtaking natural beauty, including the largest contiguous ponderosa pine forest in the world and the iconic San Francisco Peaks. The city offers a thriving outdoor recreation scene, with opportunities for hiking, skiing, and camping. Flagstaff is also home to Northern Arizona University, providing a youthful and culturally rich atmosphere. The historic downtown area offers a charming mix of locally owned shops, restaurants, and bars, along with events and festivals throughout the year. Overall, Flagstaff provides the perfect blend of outdoor adventure and small-town charm."
        />
      ),
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-kinlani.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-kinlani-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-kinlani-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-kinlani-occ.png'),
    },
  },
  // The Hickorybear
  238: {
    amenities: [
      'Hot tub',
      'Fire pit',
      'Game loft',
      'Sports court',
      'Covered patio with fireplace',
      '15 mins to Hochatown',
    ],
    propertyManagement: {
      name: 'Cabins in Broken Bow',
      description:
        'Cabins in Broken Bow started in 2001 owning multiple and managing many more cabins and properties in the Broken Bow area. The team has over 31 years of experience in property and hotel management and prides itself with a small town, cutting edge spirit. With full in-house maintenance and cleaning teams it’s no wonder they’ve accumulated 250+ great reviews on Google.',
      estimatedLaunchDate: 'May 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '25%',
    },
    market: {
      description: () => <FormattedMessage id="market-description.broken-bow" />,
      marketGradeImageSrc: getSTRImageUrl('airdna-grade-the-hickorybear.png'),
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-hickorybear-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-hickorybear-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-hickorybear-occ.png'),
    },
  },
  // The Pasquin
  255: {
    amenities: [
      'Hot tub',
      'Game room',
      'Spacious for large groups',
      'Fire pit',
      'Community center with pool & sport courts',
      '20 mins to Ski Mountain',
    ],
    propertyManagement: {
      name: 'Roseus',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      estimatedLaunchDate: 'June 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => <FormattedMessage id="market-description.poconos" />,
    },
  },
  // The Koi
  256: {
    amenities: [
      'Covered hot tub',
      'Large pool with overhead palm trees',
      'Spacious backyard for gatherings',
      'Game room',
      'Driveway parking',
      '9 min drive to the Promenade',
    ],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'July 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => <FormattedMessage id="market-description.scottsdale" />,
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-koi-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-koi-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-koi-occ.png'),
    },
  },
  // The Longbranch
  257: {
    amenities: [
      'Hot tub',
      'Fire pit',
      'Deck',
      'Game loft',
      'Modern wooden finishes',
      '5 mins to Hochatown',
      '15 mins to Broken Bow Lake',
    ],
    propertyManagement: {
      name: 'Cabins in Broken Bow',
      description:
        'Cabins in Broken Bow started in 2001 owning multiple and managing many more cabins and properties in the Broken Bow area. The team has over 31 years of experience in property and hotel management and prides itself with a small town, cutting edge spirit. With full in-house maintenance and cleaning teams it’s no wonder they’ve accumulated 250+ great reviews on Google.',
      estimatedLaunchDate: 'July 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '25%',
    },
    market: {
      description: () => <FormattedMessage id="market-description.broken-bow" />,
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-longbranch-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-longbranch-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-longbranch-occ.png'),
    },
  },
  // The Coolbaugh
  258: {
    amenities: [
      'Hot tub',
      'Game room',
      'Spacious for large groups',
      'Fire pit',
      'Community center with pool & sport courts',
      '8 mins to Ski Mountain',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'July 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: {
      description: () => <FormattedMessage id="market-description.poconos" />,
    },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-coolbaugh-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-coolbaugh-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-coolbaugh-occ.png'),
    },
  },
  // The Pinkshell
  259: {
    amenities: [
      'In-ground pool',
      'Bedroom balcony access',
      'Spacious backyard for gatherings',
      'Driveway parking',
      '3 min drive to the beach',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'June 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: { description: () => <FormattedMessage id="market-description.panama-city-beach" /> },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-pinkshell-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-pinkshell-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-pinkshell-occ.png'),
    },
  },
  // The Loop
  269: {
    amenities: [
      'Hot tub',
      'Balcony with nature views',
      'Game room',
      'Driveway parking',
      '5 min drive to downtown Gatlinburg',
    ],
    propertyManagement: {
      name: 'Roseus Hospitality',
      description: <FormattedMessage id="property-manager-description.roseus" />,
      imageSrc: getSTRImageUrl('property-management-roseus.png'),
      estimatedLaunchDate: 'June 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: { description: () => <FormattedMessage id="market-description.gatlinburg" /> },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-loop-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-loop-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-loop-occ.png'),
    },
  },
  // The Pickler
  270: {
    amenities: [
      'Regulation size pickleball court',
      'Large in-ground pool with overhead palm trees',
      'Spacious backyard for gatherings',
      'Driveway parking',
      '10 min drive to the Promenade',
    ],
    propertyManagement: {
      name: 'Old Town Rental',
      description: <FormattedMessage id="property-manager-description.old-town-rental" />,
      imageSrc: getSTRImageUrl('property-management-old-town.png'),
      estimatedLaunchDate: 'August 2023',
    },
    feeData: {
      aumPropertyPurchasePrice: '0.125',
      aumGrossRevenues: '5',
      pmGrossRentsText: '15% (up to 20% depending on the performance of the property)',
    },
    market: { description: () => <FormattedMessage id="market-description.scottsdale" /> },
    historicalData: {
      dailyRateChartImageSrc: getSTRImageUrl('charts-the-pickler-adr.png'),
      monthlyRevenueChartImageSrc: getSTRImageUrl('charts-the-pickler-mr.png'),
      occupancyChartImageSrc: getSTRImageUrl('charts-the-pickler-occ.png'),
    },
  },
};

export const useStrOfferingData = (offeringId: number) =>
  useMemo(() => {
    const offering = STR_EXTRA_DATA[offeringId];

    if (!offering) {
      return {} as STRData;
    }

    return offering;
  }, [offeringId]);

/**
 * Provided an OfferingStatus, returns whether the AirDNA charts should be shown on the Property Details Page.
 *
 * Currently we want to show these if the Offering is in a COMING_SOON/PRE_LAUNCH or transactable state.
 */
export const shouldShowAirDnaHistoricalCharts = (offeringStatus: OfferingStatus) =>
  [
    OfferingStatus.TRANSACT_ALL,
    OfferingStatus.TRANSACT_EARLY_ACCESS,
    OfferingStatus.COMING_SOON,
    OfferingStatus.TRANSACT_RESERVATIONS,
    OfferingStatus.RESERVATIONS,
    OfferingStatus.PRE_LAUNCH,
  ].includes(offeringStatus);
